import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Audi Quattro (Ur-Quattro, 1980) — the rally-bred all-wheel-drive coupe at
// true scale (1 unit = 1 metre): length 4.40, width 1.72 over the blistered
// arches, height 1.34, wheelbase 2.52 (front axle +1.28, rear -1.24), on ~15-inch
// alloys. Nose is +Z. A crisp, boxy, angular 1980s two-door NOTCHBACK coupe:
// a flat bonnet, an upright glassy cabin under a body-width roof, and a short
// SOLID boot stepped down from the roof with a subtle lip and a fairly upright
// backlight that bridges the roof down onto the boot as one connected mass (no
// open deck behind the cabin). The Quattro signatures: squared black wheel-arch
// flares standing just proud of the body at all four corners, a wide flat DLO
// with thin pillars, four rectangular headlamps (two per side) flanking a
// squarish grille with the four-rings badge (plain chrome discs, no lettering),
// slim body-colour bumpers with black rub strips, a chin spoiler and wide
// horizontal tail lamps. Body panels carry the "paint" recolour slot (Alpine
// White); `drive` spins the wheels and `hazards` blinks the six indicators.

const PAINT = "#eceeec"; // classic Alpine White — the "paint" slot default
const TRIM = "#17181b"; // gloss-black trim (nose panel, grille, window frames)
const CLAD = "#26282c"; // matte black plastic (arch flares, rub strips, chin, sills)
const WELL = "#141518"; // dark wheel wells inside the arch flares
const GLASS = "#2a3742"; // tinted side / rear glazing
const SCREEN = "#3a4c5e"; // windscreen (a shade lighter than the tint)
const CABIN = "#101114"; // dark interior block behind the glazing (no see-through)
const CHROME = "#ccd1d6";
const SILVER = "#c4c9cf"; // five-spoke alloy spokes, grille slats
const HUB = "#6b7076"; // lighter grey alloy hub face behind the spokes
const RIMDARK = "#3c3f44"; // rim barrel
const TIRE = "#1a1b1d";
const BRAKE = "#8c9196";
const LAMP = "#e8f0f4"; // headlamp glass
const AMBER = "#e8a13a";
const RED = "#a91527"; // tail lamps
const REDDARK = "#701020";
const PLATE = "#eceff2";

// Spec-sheet skeleton (metres). Track kept modest so the tyres sit just inside
// the squared arch flares, not proud of them, for a planted rally-coupe stance.
const WHEEL_R = 0.31;
const WHEEL_X = 0.72; // half track (1.44); tyre outer face at 0.84
const AXLE_F = 1.28;
const AXLE_R = -1.24; // wheelbase 2.52
const BODY_HW = 0.8; // body-tub half-width (1.60); arch flares reach ~0.86

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** One ~15-inch five-spoke alloy: tyre, brake disc, dark rim barrel, a lighter
 *  grey hub face, five bright spokes and a chrome centre cap. Every part shares
 *  `name` so the `drive` track spins them as one node about the axle. `side` is
 *  +1 for the right (+x) wheel, -1 for the left. Outer tyre face at 0.84, just
 *  inside the squared arch flare (outer 0.86). */
function wheel(side: 1 | -1, z: number, name: string): Part[] {
  const x = side * WHEEL_X;
  const y = WHEEL_R;
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, 0.24, 18, TIRE, [x, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.14, 0.14, 0.03, 16, BRAKE, [x - side * 0.06, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.235, 0.235, 0.06, 18, RIMDARK, [x + side * 0.05, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.205, 0.205, 0.03, 18, HUB, [x + side * 0.085, y, z], [0, 0, Math.PI / 2], { name }),
  ];
  for (let k = 0; k < 5; k++) {
    parts.push(
      box(0.06, 0.39, 0.05, SILVER, [x + side * 0.09, y, z], [(k * 2 * Math.PI) / 5, 0, 0], { name }),
    );
  }
  parts.push(cyl(0.06, 0.06, 0.03, 14, CHROME, [x + side * 0.1, y, z], [0, 0, Math.PI / 2], { name }));
  return parts;
}

/** One squared "blistered" arch flare + its dark wheel well. The Quattro flare
 *  is a boxy black lip (a top brow plus a short front and rear leg) standing
 *  ~0.06 proud of the white body side, tracing the wheel opening as a crisp
 *  squared arch. The dark well disc hugs the tyre so the arch gap is closed, and
 *  the tyre's outer face is proud of the disc so the alloy fills the arch. */
function flare(side: 1 | -1, z: number): Part[] {
  const lx = side * 0.83; // lip outer face 0.86 → arch width ~1.72 (spec)
  return [
    cyl(0.37, 0.37, 0.03, 20, WELL, [side * 0.81, WHEEL_R, z], [0, 0, Math.PI / 2]),
    box(0.06, 0.08, 0.78, CLAD, [lx, 0.66, z]), // squared top brow, just above the tyre
    box(0.06, 0.34, 0.08, CLAD, [lx, 0.48, z + 0.37]), // short front leg
    box(0.06, 0.34, 0.08, CLAD, [lx, 0.48, z - 0.37]), // short rear leg
  ];
}

// The six hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the parts.
// Front pair sits amber in the bumper below the outer headlamps; rear pair is
// the amber inner segment of each tail lamp; a side-repeater pair on the front
// fenders.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; size: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.7, 0.48, 2.12], size: [0.15, 0.07, 0.04] },
  { name: "indicator-fr", pos: [0.7, 0.48, 2.12], size: [0.15, 0.07, 0.04] },
  { name: "indicator-rl", pos: [-0.3, 0.72, -2.075], size: [0.14, 0.16, 0.06] },
  { name: "indicator-rr", pos: [0.3, 0.72, -2.075], size: [0.14, 0.16, 0.06] },
  { name: "indicator-ml", pos: [-0.815, 0.86, 1.55], size: [0.03, 0.05, 0.06] },
  { name: "indicator-mr", pos: [0.815, 0.86, 1.55], size: [0.03, 0.05, 0.06] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear ----------------------------------------------------------
  P.push(
    ...wheel(-1, AXLE_F, "wheel-fl"), ...wheel(1, AXLE_F, "wheel-fr"),
    ...wheel(-1, AXLE_R, "wheel-rl"), ...wheel(1, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.05, 0.05, 1.5, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2])); // axle
    for (const s of S) P.push(...flare(s, z));
  }
  P.push(box(1.52, 0.16, 3.6, TRIM, [0, 0.22, 0])); // low underbody pan, closes the profile

  // -- Body masses (paint): the boxy three-box, dropped onto the wheels -------
  // A tall solid body with a HIGH beltline (0.96): the glasshouse above is a
  // shallow band, so the body reads as a low saloon/coupe rather than a tall
  // cab, and the boot lid (also at 0.96) is just the top of that tall body, not
  // a low bed floor. This is the key to the notchback NOT reading as a pickup.
  P.push(paint(BODY_HW * 2, 0.66, 3.74, [0, 0.63, -0.02])); // main tub, sill (0.30) to shoulder (0.96)
  P.push(paint(BODY_HW * 2, 0.07, 3.3, [0, 0.96, -0.08])); // belt-line shoulder band
  P.push(paint(1.46, 0.08, 1.1, [0, 0.945, 1.4], [0.05, 0, 0])); // flat bonnet, slightly nose-down
  for (const s of S) P.push(paint(0.1, 0.09, 1.1, [s * 0.745, 0.955, 1.4], [0.05, 0, 0])); // fender tops
  P.push(paint(BODY_HW * 2, 0.52, 0.18, [0, 0.7, 1.99])); // nose block behind the fascia (0.44-0.96)
  // Short SOLID boot (the third box): a single closed mass whose flat lid top
  // sits at the high belt line (0.96), well below the roof, the tail face
  // rising solid from 0.46 up — a clear closed trunk, no open deck, no cavity
  // to look down into. Kept short so it never reads as a pickup bed, and capped
  // by a raised ducktail lip that marks the trunk edge.
  P.push(paint(BODY_HW * 2, 0.5, 0.5, [0, 0.71, -1.78])); // solid boot + tail, lid top at 0.96
  P.push(paint(1.52, 0.09, 0.14, [0, 1.0, -1.99])); // raised ducktail spoiler marking the trunk edge
  P.push(box(1.5, 0.24, 0.3, TRIM, [0, 0.4, -1.9])); // under-tail fill (no see-through)
  P.push(box(1.5, 0.24, 0.3, TRIM, [0, 0.4, 1.9])); // under-nose fill (no see-through)

  // -- Black plastic: sills, slim bumpers, chin air dam, valance --------------
  for (const s of S) P.push(box(0.05, 0.14, 2.5, CLAD, [s * 0.79, 0.32, -0.02])); // sill, arch to arch
  P.push(paint(1.6, 0.16, 0.13, [0, 0.48, 2.05])); // front bumper (body colour)
  P.push(box(1.62, 0.06, 0.05, CLAD, [0, 0.48, 2.13])); // front bumper black rub strip
  P.push(box(1.5, 0.14, 0.1, CLAD, [0, 0.35, 2.02])); // front chin spoiler
  P.push(box(1.3, 0.08, 0.05, CLAD, [0, 0.29, 2.05])); // chin lower lip
  P.push(paint(1.6, 0.16, 0.13, [0, 0.48, -2.0])); // rear bumper (body colour)
  P.push(box(1.62, 0.06, 0.05, CLAD, [0, 0.48, -2.08])); // rear bumper black rub strip
  P.push(box(1.5, 0.14, 0.1, CLAD, [0, 0.35, -1.97])); // rear valance

  // -- Greenhouse: an upright, glassy cabin — glass flush under a body-width
  // roof so roof + glass + body read as one crisp box with no ears. ----------
  const SCREEN_RAKE = 0.52; // fairly upright 1980s windscreen
  const BACK_RAKE = 0.7; // recessed backlight glass, laid back
  const SAIL_RAKE = 0.36; // solid C-pillar sails, fairly upright (shallow enough
  // that no rotated corner pokes above the roof)
  P.push(box(1.48, 0.05, 0.14, TRIM, [0, 0.965, 0.86])); // cowl at the screen base
  // Dark interior block behind ALL the glazing, so no window is see-through and
  // the cabin reads as one solid volume rather than a hollow shell.
  P.push(box(1.42, 0.3, 2.05, CABIN, [0, 1.05, -0.53]));
  P.push(box(1.44, 0.32, 0.04, SCREEN, [0, 1.12, 0.73], [-SCREEN_RAKE, 0, 0])); // upright windscreen
  for (const s of S) P.push(paint(0.08, 0.34, 0.08, [s * 0.71, 1.12, 0.73], [-SCREEN_RAKE, 0, 0])); // thin A-pillars
  // Flat roof whose front edge (0.68) overlaps the windscreen top so the cabin
  // is sealed, whose rear (-1.30) is met by the solid C-pillar sails below (no
  // overhang gap), and only slightly narrower than the body (mild tumblehome).
  P.push(paint(1.52, 0.06, 1.98, [0, 1.27, -0.31])); // roof, front 0.68 -> rear -1.30, top 1.30
  for (const s of S) {
    // Side glass tucked just inboard of the roof edge (0.745 < roof 0.76) so
    // nothing wings past the roofline in front/back views. Wide flat DLO.
    P.push(box(0.04, 0.25, 1.72, GLASS, [s * 0.745, 1.125, -0.35])); // side glass (z 0.51 -> -1.21)
    P.push(box(0.05, 0.25, 0.06, TRIM, [s * 0.75, 1.125, -0.3])); // B-pillar
    P.push(box(0.02, 0.03, 1.9, CHROME, [s * 0.802, 1.0, -0.32])); // belt-line chrome window surround
  }
  // Solid C-pillar sails: thick, fairly upright quarters that OVERLAP the
  // side-glass rear (z -1.17..-1.43) and run down to the boot, so there is no
  // gap under the roof rear and no free-standing pillar, kept shallow so no
  // rotated corner pokes above the roof. The backlight is a recessed window
  // BETWEEN them, so roof -> C-pillar/backlight -> boot read as one sealed,
  // connected rear — a closed notchback, never an open bed.
  for (const s of S) P.push(paint(0.15, 0.34, 0.26, [s * 0.72, 1.09, -1.3], [SAIL_RAKE, 0, 0])); // solid C-pillar sail
  P.push(box(1.32, 0.36, 0.03, GLASS, [0, 1.1, -1.36], [BACK_RAKE, 0, 0])); // backlight, recessed between the sails
  P.push(box(0.5, 0.025, 0.02, REDDARK, [0, 0.965, -1.62])); // thin high brake strip on the boot lid

  // -- Front fascia: four rectangular headlamps + squarish grille + four rings
  for (const s of S) {
    // One black headlamp housing per side holding two rectangular lenses (the
    // outer larger), giving the classic four-lamp face flanking the grille.
    P.push(box(0.6, 0.22, 0.04, TRIM, [s * 0.56, 0.72, 2.07])); // headlamp housing
    P.push(box(0.28, 0.17, 0.03, LAMP, [s * 0.67, 0.72, 2.09])); // outer rectangular lens
    P.push(box(0.22, 0.17, 0.03, LAMP, [s * 0.42, 0.72, 2.09])); // inner rectangular lens
  }
  P.push(box(0.5, 0.22, 0.05, TRIM, [0, 0.72, 2.07])); // squarish central grille
  for (const dy of [-0.06, 0, 0.06]) {
    P.push(box(0.46, 0.02, 0.02, SILVER, [0, 0.72 + dy, 2.1])); // horizontal grille slats
  }
  P.push(box(0.3, 0.09, 0.02, TRIM, [0, 0.9, 2.09])); // dark backing so the rings read
  for (const rx of [-0.105, -0.035, 0.035, 0.105]) {
    P.push(cyl(0.045, 0.045, 0.02, 14, CHROME, [rx, 0.9, 2.11], [Math.PI / 2, 0, 0])); // four rings (plain discs)
  }
  P.push(box(0.44, 0.11, 0.02, PLATE, [0, 0.42, 2.11])); // front plate on the lower bumper

  // -- Rear fascia: wide horizontal tail lamps spanning most of the tail ------
  for (const s of S) {
    // Wide red cluster with the amber indicator (from the table) as its inner
    // segment, on a dark backing proud of the tail face.
    P.push(box(0.58, 0.22, 0.04, TRIM, [s * 0.5, 0.72, -2.03])); // cluster backing
    P.push(box(0.56, 0.2, 0.05, RED, [s * 0.5, 0.72, -2.05])); // red tail lamp
    P.push(box(0.04, 0.06, 0.02, REDDARK, [s * 0.7, 0.52, -2.04])); // low reflector
  }
  P.push(box(0.44, 0.12, 0.02, PLATE, [0, 0.72, -2.06])); // rear plate in the centre gap
  P.push(box(0.3, 0.09, 0.02, TRIM, [0, 0.9, -2.0])); // dark backing for the rear rings
  for (const rx of [-0.105, -0.035, 0.035, 0.105]) {
    P.push(cyl(0.04, 0.04, 0.02, 14, CHROME, [rx, 0.9, -2.02], [Math.PI / 2, 0, 0])); // four rings on the boot
  }
  P.push(cyl(0.045, 0.045, 0.1, 12, CHROME, [-0.35, 0.29, -2.02], [Math.PI / 2, 0, 0])); // exhaust tip

  // -- Furniture --------------------------------------------------------------
  for (const s of S) {
    P.push(box(0.04, 0.025, 0.03, TRIM, [s * 0.81, 1.0, 0.62])); // mirror arm
    P.push(paint(0.09, 0.08, 0.1, [s * 0.86, 1.01, 0.6])); // body-colour mirror cap
    P.push(box(0.035, 0.06, 0.012, CHROME, [s * 0.86, 1.01, 0.548])); // mirror glass
    P.push(box(0.02, 0.03, 0.13, CHROME, [s * 0.805, 0.82, 0.15])); // door handle
    P.push(box(0.34, 0.012, 0.025, TRIM, [s * 0.26, 0.99, 0.8], [-SCREEN_RAKE, 0, 0])); // wiper
  }
  for (const ind of INDICATORS) {
    P.push(box(ind.size[0], ind.size[1], ind.size[2], AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X, WHEEL_R, AXLE_R] },
];

export const audiQuattro = defineModel({
  id: "audi-quattro",
  name: "Audi Quattro",
  batchId: "cars",
  tags: ["car", "audi", "quattro", "coupe", "rally", "classic"],
  summary: "A 1980 Audi Quattro (Ur-Quattro) rally coupe at true scale, in Alpine White.",
  description:
    "An Audi Quattro (Ur-Quattro, 1980) at real 1 unit = 1 metre scale (about " +
    "4.40 long, 1.72 wide over the blistered arches, 1.34 tall, 2.52 " +
    "wheelbase, on five-spoke alloys). A crisp, boxy, angular two-door " +
    "notchback coupe: a flat bonnet, an upright glassy cabin with a wide flat " +
    "DLO under a body-width roof, and a short solid boot stepped down from the " +
    "roof with a subtle lip and a fairly upright backlight bridging the roof " +
    "onto the boot as one connected mass. The Quattro signatures are all " +
    "present: squared black wheel-arch flares standing just proud of the body " +
    "at all four corners, four rectangular headlamps (two per side) flanking a " +
    "squarish grille with the four-rings badge (plain chrome discs, no " +
    "lettering), slim body-colour bumpers with black rub strips, a chin " +
    "spoiler, thin chrome window surrounds, body-colour mirrors and wide " +
    "horizontal tail lamps. Body panels carry the 'paint' recolour slot " +
    "(classic Alpine White); `drive` spins the wheels and `hazards` blinks the " +
    "six indicators.",
  author: "Fable 5",
  footprint: 2.35,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
